import { Component, ElementRef, Inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  getPayrollBrokerCategoryConflict,
  PayrollBrokerCategoryConflict,
  PayrollBrokerConflictDecision,
} from '../payroll-broker-conflicts';

export interface PayrollBroker {
  id: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  categories: string[];
}

export interface PayrollBrokerDialogData {
  broker?: PayrollBroker;
  categories: string[];
  existingBrokers: PayrollBroker[];
}

export interface PayrollBrokerDialogResult {
  broker: PayrollBroker;
  decisions: Record<string, PayrollBrokerConflictDecision>;
}

@Component({
    selector: 'app-payroll-broker-dialog',
    templateUrl: './payroll-broker-dialog.component.html',
    styleUrls: ['./payroll-broker-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollBrokerDialogComponent {
  readonly categories: string[];
  readonly existingBrokers: PayrollBroker[];

  readonly form = this.fb.group({
    companyName: ['', [Validators.required]],
    contactName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [this.usPhoneValidator]],
    categories: [[], [Validators.required]],
    confirmReassignments: [false],
  });

  constructor(
    private fb: UntypedFormBuilder,
    private elementRef: ElementRef<HTMLElement>,
    private ref: MatDialogRef<PayrollBrokerDialogComponent, PayrollBrokerDialogResult | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: PayrollBrokerDialogData,
  ) {
    this.categories = data?.categories || [];
    this.existingBrokers = data?.existingBrokers || [];

    if (data?.broker) {
      this.form.patchValue({
        companyName: data.broker.companyName || '',
        contactName: data.broker.contactName || '',
        email: data.broker.email || '',
        phone: data.broker.phone || '',
        categories: Array.isArray(data.broker.categories) ? data.broker.categories : [],
      });
    }
  }

  close(): void {
    this.ref.close(undefined);
  }

  get showCategoriesError(): boolean {
    const control = this.form.get('categories');
    return !!control && control.invalid && control.touched;
  }

  get showConflictConfirmationError(): boolean {
    const control = this.form.get('confirmReassignments');
    return this.requiresConflictConfirmation && !!control && !control.value && control.touched;
  }

  get requiresConflictConfirmation(): boolean {
    return this.selectedCategoryConflicts.length > 0;
  }

  save(): void {
    this.form.markAllAsTouched();
    const selectedCategories = this.getSelectedCategories();
    const confirmReassignments = !!this.form.get('confirmReassignments')?.value;

    if (this.requiresConflictConfirmation && !confirmReassignments) {
      this.form.get('confirmReassignments')?.markAsTouched();
    }

    if (this.form.invalid || selectedCategories.length === 0 || (this.requiresConflictConfirmation && !confirmReassignments)) {
      this.focusFirstInvalidField();
      return;
    }

    const values = this.form.getRawValue();
    const broker: PayrollBroker = {
      id: this.data?.broker?.id || this.generateId(),
      companyName: `${values.companyName || ''}`.trim(),
      contactName: `${values.contactName || ''}`.trim(),
      email: `${values.email || ''}`.trim(),
      phone: `${values.phone || ''}`.trim(),
      categories: selectedCategories,
    };

    this.ref.close({
      broker,
      decisions: this.getSelectedConflictDecisions(),
    });
  }

  onPhoneInput(): void {
    const control = this.form.get('phone');
    if (!control) {
      return;
    }

    const digits = `${control.value || ''}`.replace(/\D/g, '').slice(0, 10);
    control.setValue(this.formatUsPhone(digits), { emitEvent: false });
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: false });
  }

  selectAllCategories(): void {
    this.form.get('categories')?.setValue([...this.categories]);
    this.resetConflictConfirmation();
  }

  clearCategories(): void {
    this.form.get('categories')?.setValue([]);
    this.resetConflictConfirmation();
  }

  hasCategory(category: string): boolean {
    return this.getSelectedCategories().includes(category);
  }

  hasCategoryConflict(category: string): boolean {
    return !!this.getCategoryConflict(category);
  }

  getCategoryConflictLabel(category: string): string {
    const conflict = this.getCategoryConflict(category);
    return conflict ? `Assigned to ${conflict.currentBrokerName}` : '';
  }

  get selectedCategoryConflicts(): PayrollBrokerCategoryConflict[] {
    return this.getSelectedCategories()
      .map(category => this.getCategoryConflict(category))
      .filter((conflict): conflict is PayrollBrokerCategoryConflict => !!conflict);
  }

  toggleCategory(category: string): void {
    const selected = this.getSelectedCategories();
    const exists = selected.includes(category);
    const next = exists ? selected.filter(item => item !== category) : [...selected, category];
    this.form.get('categories')?.setValue(next);
    this.resetConflictConfirmation();
  }

  private getSelectedCategories(): string[] {
    const value = this.form.get('categories')?.value;
    return Array.isArray(value) ? value.filter(Boolean) : [];
  }

  get selectedCategoriesCount(): number {
    return this.getSelectedCategories().length;
  }

  private getSelectedConflictDecisions(): Record<string, PayrollBrokerConflictDecision> {
    return this.selectedCategoryConflicts.reduce((result, conflict) => {
      result[conflict.category] = 'move';
      return result;
    }, {} as Record<string, PayrollBrokerConflictDecision>);
  }

  private resetConflictConfirmation(): void {
    this.form.get('confirmReassignments')?.setValue(false);
    this.form.get('confirmReassignments')?.markAsPristine();
    this.form.get('confirmReassignments')?.markAsUntouched();
  }

  private focusFirstInvalidField(): void {
    const host = this.elementRef.nativeElement;
    const firstInvalidControlName = ['companyName', 'contactName', 'email', 'phone']
      .find(controlName => this.form.get(controlName)?.invalid);

    if (firstInvalidControlName) {
      this.scrollToElement(`[formControlName="${firstInvalidControlName}"]`);
      return;
    }

    if (this.showCategoriesError) {
      this.scrollToElement('.categories-grid');
      return;
    }

    if (this.showConflictConfirmationError) {
      const confirmElement =
        host.querySelector('[formControlName="confirmReassignments"] .mat-checkbox-input') ||
        host.querySelector('[formControlName="confirmReassignments"] input') ||
        host.querySelector('[formControlName="confirmReassignments"]');
      this.scrollToNode(confirmElement as HTMLElement | null);
    }
  }

  private scrollToElement(selector: string): void {
    this.scrollToNode(this.elementRef.nativeElement.querySelector(selector) as HTMLElement | null);
  }

  private scrollToNode(node: HTMLElement | null): void {
    if (!node) {
      return;
    }

    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (typeof node.focus === 'function') {
      setTimeout(() => node.focus({ preventScroll: true }), 0);
    }
  }

  private usPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = `${control.value || ''}`.trim();
    if (!value) {
      return null;
    }

    if (/[^0-9().\-\s]/.test(value)) {
      return { usPhone: true };
    }

    const digits = value.replace(/\D/g, '');
    return digits.length === 10 ? null : { usPhone: true };
  }

  private formatUsPhone(digits: string): string {
    if (digits.length <= 3) {
      return digits;
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }

  private getCategoryConflict(category: string): PayrollBrokerCategoryConflict | undefined {
    return getPayrollBrokerCategoryConflict(
      this.existingBrokers,
      this.data?.broker?.id || '',
      `${this.form.get('companyName')?.value || this.data?.broker?.companyName || 'this broker'}`.trim(),
      category,
    );
  }

  private generateId(): string {
    return `broker-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
}
